/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Injectable } from '@angular/core';
import { MediaObserver } from '@ngbracket/ngx-layout';
import { LoggerService } from './logger.service';
import { UAParser } from 'ua-parser-js';
import { from, of } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { WindowRefService } from '../../../core/ui/services/windowRef.service';

@Injectable()
export class DeviceService {
    private _hasTouchEvents: boolean;
    private _uaParser: UAParser;
    private localStream: MediaStream;

    constructor(private logger: LoggerService, private media: MediaObserver, private windowRef: WindowRefService) {
        const ua = this.windowRef.nativeWindow.navigator.userAgent;
        this._uaParser = new UAParser(ua);
    }

    matchesLayoutSize(layoutSize) {
        if ('xs' === layoutSize) {
            return true;
        } else {
            return this.media.isActive(layoutSize);
        }
    }

    enableCamera(videoElement: HTMLVideoElement, options: MediaStreamConstraints = {}) {
        const constraints = { ...{ video: true }, ...options };
        return from(window.navigator.mediaDevices.getUserMedia(constraints)).pipe(
            map((stream) => {
                this.localStream = stream;
                videoElement.srcObject = stream;
                return true;
            }),
            catchError((err) => {
                this.logger.error(err);
                return of(false);
            }),
            take(1)
        );
    }

    disableCamera(videoElement: HTMLVideoElement) {
        videoElement.pause();
        videoElement.src = '';
        if (this.localStream) {
            this.localStream.getTracks()[0].stop();
            this.localStream = null;
        }
    }

    get isPortrait() {
        return this.media.isActive('portrait');
    }

    get hasTouchEvents(): boolean {
        if (null === this._hasTouchEvents || undefined === this._hasTouchEvents) {
            this._hasTouchEvents =
                'ontouchstart' in window ||
                0 < (window as Window).navigator.maxTouchPoints ||
                false;
        }
        return this._hasTouchEvents;
    }

    get isIOS() {
        return (window as any).Modernizr.isios;
    }

    get isFirefox() {
        return this._uaParser.getBrowser().name === 'Firefox';
    }

    get hasPointerEvents() {
        return (window as any).Modernizr.pointerevents;
    }

    get canCaptureUserMedia() {
        return (window as any).Modernizr.getusermedia;
    }

    get hasVideoInput$() {
        return from(navigator.mediaDevices.enumerateDevices()).pipe(
            map((devices) => {
                const videoDevices = devices.filter((device) => device.kind === 'videoinput');
                return videoDevices.length > 0;
            })
        );
    }

    /**
     * @deprecated
     */
    get isMobile() {
        // TODO: correct logic for DeviceService.isMobile
        return false;
    }
}
